// services/v3/cache/tabSync.ts — cross-tab notifications (SPEC §5)
// BroadcastChannel 'xtable-v3' when available, otherwise `storage` events on
// ping keys. Every subscribe function returns an unsubscribe.
import type { SessionEndReason } from '~/types/v3/session'
import type { Unsubscribe } from '~/types/v3/datasource'
import { SESSION_END_KEY } from './sessionStorage'

const CHANNEL_NAME = 'xtable-v3'
/** storage-event fallback ping key for cart changes */
const CART_PING_KEY = 'xtable.v3.cart.ping'

type SessionClearedMsg = { type: 'session-cleared'; reason: SessionEndReason }
type CartChangedMsg = { type: 'cart-changed'; key: string }
type Msg = SessionClearedMsg | CartChangedMsg

const noop: Unsubscribe = () => {}

const hasWindow = (): boolean => typeof window !== 'undefined'

function warn(op: string, err: unknown): void {
  try {
    console.warn(`[v3/tabSync] ${op} failed`, err)
  } catch {
    /* noop */
  }
}

function bcSupported(): boolean {
  if (!hasWindow()) return false
  try {
    return typeof BroadcastChannel !== 'undefined'
  } catch {
    return false
  }
}

let channel: BroadcastChannel | null = null
function getChannel(): BroadcastChannel | null {
  if (!bcSupported()) return null
  if (channel) return channel
  try {
    channel = new BroadcastChannel(CHANNEL_NAME)
    return channel
  } catch (err) {
    warn('open channel', err)
    return null
  }
}

function post(msg: Msg): boolean {
  const ch = getChannel()
  if (!ch) return false
  try {
    ch.postMessage(msg)
    return true
  } catch (err) {
    warn('postMessage', err)
    return false
  }
}

function asMsg(data: unknown): Msg | null {
  if (typeof data !== 'object' || data === null) return null
  const m = data as Record<string, unknown>
  if (m.type === 'session-cleared' && typeof m.reason === 'string') {
    return { type: 'session-cleared', reason: m.reason as SessionEndReason }
  }
  if (m.type === 'cart-changed' && typeof m.key === 'string') {
    return { type: 'cart-changed', key: m.key }
  }
  return null
}

function onChannelMessage(handler: (m: Msg) => void): Unsubscribe {
  const ch = getChannel()
  if (!ch) return noop
  const listener = (ev: MessageEvent) => {
    const m = asMsg(ev.data)
    if (m) {
      try {
        handler(m)
      } catch (err) {
        warn('handler', err)
      }
    }
  }
  try {
    ch.addEventListener('message', listener)
  } catch (err) {
    warn('addEventListener(message)', err)
    return noop
  }
  return () => {
    try {
      ch.removeEventListener('message', listener)
    } catch (err) {
      warn('removeEventListener(message)', err)
    }
  }
}

function onStorage(key: string, handler: (newValue: string | null) => void): Unsubscribe {
  if (!hasWindow()) return noop
  const listener = (ev: StorageEvent) => {
    if (ev.key !== key) return
    try {
      handler(ev.newValue)
    } catch (err) {
      warn('handler', err)
    }
  }
  try {
    window.addEventListener('storage', listener)
  } catch (err) {
    warn('addEventListener(storage)', err)
    return noop
  }
  return () => {
    try {
      window.removeEventListener('storage', listener)
    } catch (err) {
      warn('removeEventListener(storage)', err)
    }
  }
}

/* ------------------------------------------------------------------ */
/* public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Notify other tabs that the session ended. Called by `clearSession()`;
 * with no BroadcastChannel the `xtable.v3.session.end` write already raises a
 * `storage` event in the other tabs, so nothing more is needed.
 */
export function broadcastSessionCleared(reason: SessionEndReason): void {
  post({ type: 'session-cleared', reason })
}

/** Subscribe to session-cleared notifications from other tabs. */
export function onSessionCleared(cb: (reason: SessionEndReason) => void): Unsubscribe {
  if (!hasWindow()) return noop
  if (bcSupported()) {
    return onChannelMessage((m) => {
      if (m.type === 'session-cleared') cb(m.reason)
    })
  }
  return onStorage(SESSION_END_KEY, (v) => {
    if (v) cb(v as SessionEndReason)
  })
}

/** Notify other tabs that the cart under `key` changed. */
export function broadcastCart(key: string): void {
  if (!hasWindow() || !key) return
  if (post({ type: 'cart-changed', key })) return
  try {
    window.localStorage.setItem(CART_PING_KEY, JSON.stringify({ key, t: Date.now() }))
  } catch (err) {
    warn('broadcastCart', err)
  }
}

/** Subscribe to cart-changed notifications from other tabs. */
export function onCartChanged(cb: (key: string) => void): Unsubscribe {
  if (!hasWindow()) return noop
  if (bcSupported()) {
    return onChannelMessage((m) => {
      if (m.type === 'cart-changed') cb(m.key)
    })
  }
  return onStorage(CART_PING_KEY, (v) => {
    if (!v) return
    try {
      const parsed: unknown = JSON.parse(v)
      if (typeof parsed === 'object' && parsed !== null) {
        const k = (parsed as Record<string, unknown>).key
        if (typeof k === 'string') cb(k)
      }
    } catch (err) {
      warn('onCartChanged parse', err)
    }
  })
}

/** Closes the shared channel (tests / teardown). */
export function closeTabSync(): void {
  if (!channel) return
  try {
    channel.close()
  } catch (err) {
    warn('close', err)
  }
  channel = null
}
